/**
 * Shared nav-link shapes and helpers for every header variant.
 *
 * Extracted from Header.tsx so a second header (the Looks system's
 * `floating_glass` variant) composes the same nav assembly, link renderers
 * and actions instead of carrying a copy of them.
 */
import type { AppManifestNavEntry, AppMount } from '@/lib/apps-mounts';
import { isDesigner } from '@/lib/roles';
import { DESIGNER_HOME } from '@/config/designer-surface';

/**
 * Unified nav link shape (contract v1.6). The Header maps marketing links,
 * app manifest entries, and runtime-contributed entries into this same shape
 * so the render functions don't need to branch by source.
 */
export interface NavLink {
  label: string;
  href?: string;
  axissOpen?: boolean;
  axissView?: 'home' | 'chat';
  hide_when_authed?: boolean;
  badge?: { text: string; tone?: 'neutral' | 'accent' | 'warning' };
  children?: NavLink[];
  /** Icon name from the app manifest / runtime nav contribution (v1.6).
   *  Kebab-case lucide name; unknown values render no glyph. */
  icon?: string;
}

/** An installed app's pages, grouped under the app's name in account menus. */
export interface AppNavSection {
  appId: string;
  appName: string;
  items: NavLink[];
}

/**
 * Where the "Admin panel" link points. A designer sent to /admin would be
 * bounced straight back out by AdminLayout, so point them at a page their role
 * can actually open.
 */
export function adminLinkDest(role: string | null | undefined): string {
  return isDesigner(role) ? DESIGNER_HOME : '/admin';
}

/** Translate a manifest nav entry to a host-shell link, prefixing the path
 *  with the mount's `path_prefix`. When the entry's `path` matches another
 *  mount's `default_path` (e.g. multi-mount apps), we link to that mount
 *  instead so the user lands at the canonical bare prefix. */
export function manifestEntryToNavLink(
  entry: AppManifestNavEntry,
  primaryMount: AppMount,
  allMountsForApp: AppMount[],
): NavLink {
  const matched = allMountsForApp.find((m) => m.default_path === entry.path);
  const href = matched
    ? `/${matched.path_prefix}`
    : `/${primaryMount.path_prefix}${entry.path === '/' ? '' : entry.path}`;
  return {
    label: entry.label,
    href,
    badge: entry.badge,
    icon: entry.icon,
    children: entry.children?.map((c) => manifestEntryToNavLink(c, primaryMount, allMountsForApp)),
  };
}

export function badgeToneClass(tone: 'neutral' | 'accent' | 'warning' | undefined): string {
  switch (tone) {
    case 'accent':
      return 'bg-terra/15 text-terra-700';
    case 'warning':
      return 'bg-amber-100 text-amber-800';
    case 'neutral':
    default:
      return 'bg-sage-100 text-sage-800';
  }
}
